import dayjs from 'dayjs';
import {GoalProgress} from '@utils/proportions';
import {
  goalAttentionStatus,
  goalDisplayPerformance,
  goalIsEnded,
  goalIsOverdue,
  goalIsUpcoming,
  savedGoalPerformance,
  suggestedGoalPerformance,
  SuggestedGoalPerformance,
} from '@utils/goal-status';
import {
  MyGoalsFilters,
  MyGoalsLifecycleFilter,
  MyGoalsPerformanceFilter,
  MyGoalsPeriod,
  MyGoalsScopeFilter,
  MyGoalsSort,
} from './my-goals-v2.types';

export const periodFromValue = (value: any): MyGoalsPeriod => {
  const now = dayjs();
  const fallbackStart = now
    .month(Math.floor(now.month() / 3) * 3)
    .startOf('month');
  const starts = value?.starts || fallbackStart.format('MM/DD/YYYY');
  const ends =
    value?.ends ||
    fallbackStart.add(2, 'month').endOf('month').format('MM/DD/YYYY');
  const label = value?.period || value?.label || '';
  const match = String(label).match(/Q([1-4])\s+(\d{4})/);
  const startDate = dayjs(starts);
  const quarter = match
    ? Number(match[1])
    : Math.floor(startDate.month() / 3) + 1;
  const year = match ? Number(match[2]) : startDate.year();
  return {starts, ends, label: `Q${quarter} ${year}`, quarter, year};
};

export const standardQuarter = (
  year: number,
  quarter: number,
): MyGoalsPeriod => {
  const starts = dayjs(new Date(year, (quarter - 1) * 3, 1));
  return {
    starts: starts.format('MM/DD/YYYY'),
    ends: starts.add(2, 'month').endOf('month').format('MM/DD/YYYY'),
    label: `Q${quarter} ${year}`,
    year,
    quarter,
  };
};

export {goalIsEnded, goalIsOverdue};

export const goalsFromResponse = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.goals)) return response.goals;
  return [];
};

export const goalMatchesSearch = (goal: any, searchValue: string): boolean => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return true;
  return [
    goal?.name,
    goal?.title,
    ...(goal?.keyResults || []).map(
      (keyResult: any) => keyResult?.name || keyResult?.title,
    ),
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
};

export const goalProgress = (goal: any): number => {
  const calculated = Number(GoalProgress(goal).percentage);
  const value = Number.isFinite(calculated) ? calculated : 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

export const goalCardDueLabel = (
  goal: any,
  progress: number,
  today = dayjs(),
): string => {
  if (goalIsEnded(goal)) return 'Ended';

  const endDate = goal?.endDate ? dayjs(goal.endDate) : undefined;
  if (goalIsOverdue(goal, today)) {
    return endDate ? `Due ${endDate.format('MMM D')}` : 'Past due';
  }
  if (!endDate) return `${progress}%`;

  const months = endDate.diff(today, 'month');
  const days = endDate.diff(today, 'day');
  return months >= 1
    ? `Due in ${months} month${months === 1 ? '' : 's'}`
    : `Due in ${Math.max(0, days)} day${days === 1 ? '' : 's'}`;
};

export const goalScope = (goal: any): MyGoalsScopeFilter => {
  const type = String(goal?.goalType || '').toLowerCase();
  if (type === 'company' || type === 'company-wide') return 'company';
  if (type === 'group' || type === 'team') return 'team';
  return 'personal';
};

export const goalPerformance = (
  goal: any,
  today = dayjs(),
): MyGoalsPerformanceFilter => goalDisplayPerformance(goal, today);

export const goalIsActive = (goal: any, today = dayjs()): boolean => {
  return (
    !goalIsEnded(goal) &&
    !goalIsUpcoming(goal, today) &&
    !goalIsOverdue(goal, today)
  );
};

export const goalStatusSuggestion = (
  goal: any,
  today = dayjs(),
): SuggestedGoalPerformance | undefined => {
  if (!goalIsActive(goal, today)) return undefined;
  const suggestion = suggestedGoalPerformance(goal, today);
  return goalPerformance(goal) === suggestion ? undefined : suggestion;
};

export const goalOverlapsPeriod = (
  goal: any,
  period: MyGoalsPeriod,
): boolean => {
  if (!goal?.startDate && !goal?.endDate) return true;
  const goalStart = dayjs(goal?.startDate || goal?.endDate);
  const goalEnd = dayjs(goal?.endDate || goal?.startDate);
  const periodStart = dayjs(period.starts).startOf('day');
  const periodEnd = dayjs(period.ends).endOf('day');
  return !goalEnd.isBefore(periodStart) && !goalStart.isAfter(periodEnd);
};

export const goalIsOverdueCarryover = (
  goal: any,
  period: MyGoalsPeriod,
  today = dayjs(),
): boolean => {
  if (!goal?.endDate || !goalIsOverdue(goal, today)) return false;
  const periodStart = dayjs(period.starts).startOf('day');
  return (
    !periodStart.isAfter(today, 'day') &&
    dayjs(goal.endDate).endOf('day').isBefore(periodStart)
  );
};

export const filterMyGoals = ({
  goals,
  period,
  filters,
  lifecycle,
  includeOverdueCarryover = false,
  today = dayjs(),
}: {
  goals: any[];
  period: MyGoalsPeriod;
  filters: MyGoalsFilters;
  lifecycle: MyGoalsLifecycleFilter;
  includeOverdueCarryover?: boolean;
  today?: dayjs.Dayjs;
}) =>
  goals.filter((goal) => {
    if (
      !goalOverlapsPeriod(goal, period) &&
      !(includeOverdueCarryover && goalIsOverdueCarryover(goal, period, today))
    )
      return false;

    const type = goal?.isKpi ? 'kpi' : 'objective';
    if (filters.types.length && !filters.types.includes(type)) return false;
    if (filters.scopes.length && !filters.scopes.includes(goalScope(goal))) {
      return false;
    }

    const performance = goalAttentionStatus(goal, today);
    if (
      filters.performance.length &&
      (performance === 'overdue' || !filters.performance.includes(performance))
    ) {
      return false;
    }

    if (lifecycle === 'active' && !goalIsActive(goal, today)) return false;
    if (lifecycle === 'ended' && !goalIsEnded(goal)) return false;
    if (lifecycle === 'overdue' && !goalIsOverdue(goal, today)) return false;
    return true;
  });

export const sortMyGoals = (goals: any[], sort: MyGoalsSort) =>
  [...goals].sort((first, second) => {
    if (sort === 'highest_progress') {
      return goalProgress(second) - goalProgress(first);
    }

    const firstDate = dayjs(
      sort === 'due_date' ? first?.endDate : first?.createdAt,
    ).valueOf();
    const secondDate = dayjs(
      sort === 'due_date' ? second?.endDate : second?.createdAt,
    ).valueOf();

    if (!Number.isFinite(firstDate)) return 1;
    if (!Number.isFinite(secondDate)) return -1;
    return sort === 'oldest' || sort === 'due_date'
      ? firstDate - secondDate
      : secondDate - firstDate;
  });

export const summarizeMyGoals = (goals: any[], today = dayjs()) => {
  const total = goals.length;
  const averageProgress = total
    ? Math.round(
        goals.reduce((sum, goal) => sum + goalProgress(goal), 0) / total,
      )
    : 0;

  const active = goals.filter((goal) => goalIsActive(goal, today)).length;
  const ended = goals.filter(goalIsEnded).length;
  const overdue = goals.filter((goal) => goalIsOverdue(goal, today)).length;
  const dueSoon = goals.filter((goal) => {
    if (!goal?.endDate || goalIsEnded(goal)) return false;
    const dueDate = dayjs(goal.endDate).endOf('day');
    return (
      !dueDate.isBefore(today.startOf('day')) &&
      dueDate.diff(today, 'day') <= 14
    );
  }).length;

  const byPerformance = goals.reduce(
    (summary, goal) => {
      const performance = goalAttentionStatus(goal, today);
      summary[performance] += 1;
      return summary;
    },
    {overdue: 0, on_track: 0, at_risk: 0, behind: 0, upcoming: 0, no_status: 0},
  );
  const byHealth = goals.reduce(
    (summary, goal) => {
      const health = savedGoalPerformance(goal);
      if (health) summary[health] += 1;
      return summary;
    },
    {behind: 0, at_risk: 0, on_track: 0},
  );

  return {
    total,
    averageProgress,
    active,
    ended,
    overdue,
    dueSoon,
    byPerformance,
    byHealth,
  };
};

export const activeFilterCount = (filters: MyGoalsFilters) =>
  filters.types.length + filters.scopes.length + filters.performance.length;

const collectionSize = (value: any): number => {
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (value && typeof value === 'object') return Object.keys(value).length;
  return 0;
};

export interface GoalEngagementSummary {
  commentCount: number;
  unreadCommentCount: number;
  actionItemCount: number;
  openActionItemCount: number;
  overdueActionItemCount: number;
  dueTodayActionItemCount: number;
  dueSoonActionItemCount: number;
  unreadActionItemCount: number;
  allActionItemsCompleted: boolean;
}

export const summarizeGoalEngagement = ({
  goal,
  firebaseGoal,
  mentions,
  actionItems,
  commentsLoading = false,
  actionItemsLoading = false,
  today = dayjs(),
}: {
  goal: any;
  firebaseGoal?: any;
  mentions?: any;
  actionItems: any[];
  commentsLoading?: boolean;
  actionItemsLoading?: boolean;
  today?: dayjs.Dayjs;
}): GoalEngagementSummary => {
  const fallbackCommentCount = Number(
    goal?.commentCount ??
      goal?.comments?.length ??
      goal?.activities?.filter((activity: any) => activity?.type === 'comment')
        .length ??
      0,
  );
  const fallbackActionItemCount = Number(
    goal?.taskCount ?? goal?.tasks?.length ?? 0,
  );
  const goalActionItems = actionItems.filter(
    (item) => item?.sourceId === goal?.id,
  );
  const openActionItems = goalActionItems.filter((item) => !item?.completed);
  const startOfToday = today.startOf('day');
  const dueActionItems = openActionItems.filter((item) => item?.dueDate);
  const overdueActionItemCount = dueActionItems.filter((item) =>
    dayjs(item.dueDate).startOf('day').isBefore(startOfToday),
  ).length;
  const dueTodayActionItemCount = dueActionItems.filter((item) =>
    dayjs(item.dueDate).isSame(startOfToday, 'day'),
  ).length;
  const dueSoonActionItemCount = dueActionItems.filter((item) => {
    const dueDate = dayjs(item.dueDate).startOf('day');
    const daysUntilDue = dueDate.diff(startOfToday, 'day');
    return daysUntilDue > 0 && daysUntilDue <= 3;
  }).length;
  const actionItemCount = actionItemsLoading
    ? fallbackActionItemCount
    : goalActionItems.length;
  const openActionItemCount = actionItemsLoading
    ? fallbackActionItemCount
    : openActionItems.length;

  return {
    commentCount: commentsLoading
      ? fallbackCommentCount
      : collectionSize(firebaseGoal?.comments),
    unreadCommentCount: Number(mentions?.unreadComment || 0),
    actionItemCount,
    openActionItemCount,
    overdueActionItemCount,
    dueTodayActionItemCount,
    dueSoonActionItemCount,
    unreadActionItemCount: Number(mentions?.unreadTasks || 0),
    allActionItemsCompleted:
      !actionItemsLoading && actionItemCount > 0 && openActionItemCount === 0,
  };
};
